import React, { FunctionComponent, useRef } from 'react'
import capitalize from 'lodash/capitalize'
import startCase from 'lodash/startCase'
import { Link } from 'react-router-dom'

import caution from '../../assets/images/icon-caution.svg'
import { displayDollars } from '../../helpers/displayFormatHelper'
import { utcToLocalDate } from '../../helpers/timeHelper'
import { RefiTransactionsCard } from './refiTransactionsCard'
import { RefiTransactionsRow } from './refiTransactionsRow'
import { UserAddressCard } from '../users/userAddresses/userAddressCard'
import { RefiTransaction } from './types'
import LoaderButton from '../../components/loaderButton'
import { continueRefiStatuses } from '../refiApp/refiActions'

const getExceptions = (refiTransaction: RefiTransaction) => {
  let exceptionsArray: string[] = []
  const {
    vehicle: { fraudCheck },
  } = refiTransaction

  Object.keys(fraudCheck).forEach((key) => {
    if (fraudCheck[key] && fraudCheck[key] !== 'reviewRequired') {
      exceptionsArray.push(startCase(key.toString()))
    }
  })

  return exceptionsArray.map((exception, i) => {
    return (
      <div key={i}>
        <img src={caution} alt="" />
        {exception}
      </div>
    )
  })
}

export interface Props {
  refiTransaction: RefiTransaction
}

export const RefiTransactionsInfo: FunctionComponent<Props> = ({
  refiTransaction,
}) => {
  const { id, loan, currentLoan, vehicle } = refiTransaction

  const linkRef: any = useRef(null)

  const handleClick = () => {
    linkRef.current.click()
  }

  const ContinueButton = () => {
    return (
      <div style={{ marginLeft: '12px', display: 'inline' }}>
        <LoaderButton
          type="button"
          className="button button-drive-america continue-application-drive-america"
          onClick={handleClick}
          style={{ maxWidth: '235px' }}
        >
          Continue Application
        </LoaderButton>
        <a
          href={`/refiapp?refiId=${id}`}
          ref={linkRef}
          style={{ display: 'none' }}
        >
          Continue Application
        </a>
      </div>
    )
  }

  const renderRefiHeader = (refiTransaction: RefiTransaction) => {
    const { id, status } = refiTransaction

    return (
      <div className="refi-transactions-info__header">
        Refinance id: {id}
        {continueRefiStatuses.includes(status) && <ContinueButton />}
      </div>
    )
  }

  return (
    <div className="refi-transactions-info">
      {renderRefiHeader(refiTransaction)}

      <div className="refi-transactions-info__information">
        <RefiTransactionsCard title="PREQUALIFICATION DETAILS">
          <div className="refi-transactions-info__card">
            <RefiTransactionsRow field="Tier" fieldsBold>
              {loan.tier ? loan.tier : 'N/A'}
            </RefiTransactionsRow>
            <RefiTransactionsRow field="Loan status" fieldsBold>
              {loan.status ? startCase(capitalize(loan.status)) : 'N/A'}
            </RefiTransactionsRow>
            <RefiTransactionsRow field="Decision Date" fieldsBold>
              {loan.lastDecisionAt
                ? utcToLocalDate(loan.lastDecisionAt)
                : 'N/A'}
            </RefiTransactionsRow>
            <RefiTransactionsRow field="Current APR" fieldsBold>
              {currentLoan && currentLoan.apr ? `${currentLoan.apr}%` : 'N/A'}
            </RefiTransactionsRow>
            <RefiTransactionsRow field="Blinker APR" fieldsBold>
              {loan.apr ? `${loan.apr}` : 'N/A'}
            </RefiTransactionsRow>
          </div>
        </RefiTransactionsCard>

        <RefiTransactionsCard title="VEHICLE INFORMATION">
          <div className="refi-transactions-info__card">
            <RefiTransactionsRow field="Estimated value" fieldsBold>
              {vehicle.latestValue
                ? displayDollars(vehicle.latestValue)
                : 'N/A'}
            </RefiTransactionsRow>
            <RefiTransactionsRow field="Year / Make / Model" fieldsBold>
              {vehicle.year && vehicle.make && vehicle.model ? (
                <Link to={`/vehicles/${vehicle.id}`}>
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </Link>
              ) : (
                'N/A'
              )}
            </RefiTransactionsRow>
            <RefiTransactionsRow field="VIN" fieldsBold>
              {vehicle.vin ? vehicle.vin : 'N/A'}
            </RefiTransactionsRow>
            <RefiTransactionsRow field="Trim" fieldsBold>
              {vehicle.selected_trim && vehicle.selected_trim.series
                ? vehicle.selected_trim.series
                : 'N/A'}
            </RefiTransactionsRow>
            <RefiTransactionsRow field="Mileage" fieldsBold>
              {vehicle.mileage
                ? `${vehicle.mileage.toLocaleString()} miles`
                : 'N/A'}
            </RefiTransactionsRow>
            <RefiTransactionsRow field="Vehicle history" fieldsBold>
              {vehicle.healthReport && vehicle.healthReport.url ? (
                <a href={`${vehicle.healthReport.url}`} target="_blank">
                  Carfax
                </a>
              ) : (
                'N/A'
              )}
            </RefiTransactionsRow>
            {vehicle.fraudCheck &&
            vehicle.fraudCheck.reviewRequired === true ? (
              <RefiTransactionsRow field="Exception Found" fieldsBold>
                {vehicle.fraudCheck &&
                vehicle.fraudCheck.reviewRequired === true ? (
                  <div>{getExceptions(refiTransaction)}</div>
                ) : null}
              </RefiTransactionsRow>
            ) : null}
          </div>
        </RefiTransactionsCard>

        <UserAddressCard
          noHover
          badge="Refinance Address"
          address={refiTransaction.address}
        />
      </div>
    </div>
  )
}

RefiTransactionsInfo.displayName = 'RefiTransactionsInfo'
